import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/PageHeader';
import { Zap, RefreshCw, Mail, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useEnforcement } from '@/hooks/useEnforcement';
import { isFreeAutomationGateActive } from '@/lib/freeAutomationGate';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type ToggleKey = 'auto_optimize_new_enabled' | 'auto_optimize_drift_enabled' | 'automation_emails_enabled';
type FrequencyKey = 'auto_optimize_new_frequency' | 'auto_optimize_drift_frequency';
type UploadKey = 'auto_upload_new_enabled' | 'auto_upload_drift_enabled';
type Frequency = 'daily' | 'weekly' | 'monthly';

// Mirrors the DB defaults: automation opt-out; uploads + emails opt-in.
const DEFAULT_PREFS: Record<ToggleKey, boolean> = {
  auto_optimize_new_enabled: true,
  auto_optimize_drift_enabled: true,
  automation_emails_enabled: false,
};
const DEFAULT_FREQS: Record<FrequencyKey, Frequency> = {
  auto_optimize_new_frequency: 'monthly',
  auto_optimize_drift_frequency: 'monthly',
};
const DEFAULT_UPLOADS: Record<UploadKey, boolean> = {
  auto_upload_new_enabled: false,
  auto_upload_drift_enabled: false,
};

const FREQUENCY_OPTIONS: Array<{ value: Frequency; labelKey: string }> = [
  { value: 'daily', labelKey: 'schedules.daily' },
  { value: 'weekly', labelKey: 'schedules.weekly' },
  { value: 'monthly', labelKey: 'schedules.monthly' },
];

const isFrequency = (v: unknown): v is Frequency => v === 'daily' || v === 'weekly' || v === 'monthly';

/**
 * Die beiden Query-Keys, die dieselben zwei Spalten lesen, die diese Seite
 * schreibt: `useJourney` (Fortschrittsleiste + Vertrauenszeile der
 * Feed-Check-CTAs) und `useFeedExperiment` (Aussage in der Experiment-Karte).
 * Beide stehen auf `staleTime: 5 * 60 * 1000`.
 *
 * Ohne Invalidierung nach dem Schreiben rechnet die Leiter bis zu fuenf Minuten
 * mit dem ALTEN Haken: die Freigabe-Stufe kann in dem Fenster als
 * "uebersprungen" ausgewiesen sein, obwohl der Nutzer den Haken gerade
 * abgeschaltet hat, und die Vertrauenszeile verspricht die Werkbank-Pruefung
 * (`feedCheck.cta.noCard`) oder eben nicht — jeweils falsch. Bis zu diesem
 * Nachfasser gab es in dieser Datei ueberhaupt keinen `queryClient`, der
 * Schreibpfad war also gegenueber jedem Leser stumm.
 *
 * `['profile-auto-upload']` deckt per Praefix-Abgleich auch den
 * nutzergescopten Schluessel `['profile-auto-upload', userId]` ab;
 * `-enabled` ist ein EIGENER Schluessel (anderes Element 0) und wird vom
 * Praefix nicht mitgetroffen — deshalb beide.
 */
const AUTO_UPLOAD_QUERY_KEYS = [['profile-auto-upload'], ['profile-auto-upload-enabled']] as const;

/** Schreibt dieser Patch einen der beiden Auto-Upload-Haken? */
const touchesAutoUpload = (patch: Record<string, boolean | string>): boolean =>
  'auto_upload_new_enabled' in patch || 'auto_upload_drift_enabled' in patch;

interface SchedulesProps {
  /** Rendered inside the Settings "Automatisierung" tab — heading becomes an h2 section title instead of the page h1. */
  embedded?: boolean;
}

export default function Schedules({ embedded = false }: SchedulesProps) {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const { plan, planKnown, isLoading: enforcementLoading } = useEnforcement();
  const queryClient = useQueryClient();
  const [prefs, setPrefs] = useState<Record<ToggleKey, boolean>>(DEFAULT_PREFS);
  const [freqs, setFreqs] = useState<Record<FrequencyKey, Frequency>>(DEFAULT_FREQS);
  const [uploads, setUploads] = useState<Record<UploadKey, boolean>>(DEFAULT_UPLOADS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  // Preisumbau: ab dem Env-Stichtag (VITE_FREE_AUTOMATION_GATE_AT) ist die
  // Automatik fuer Free-Konten gesperrt — Server-Pendant ist
  // FREE_AUTOMATION_GATE_AT in _shared/free-automation-gate.ts. Gate inaktiv
  // (unset/leer/unparsebar) → heutiges Verhalten fuer alle, auch Free.
  // `!enforcementLoading` haelt die Sperre zurueck, solange der Plan noch
  // laedt: ein zahlender Kunde darf nicht kurz gesperrte Schalter sehen, und
  // das harte Gate ist ohnehin der Server (der die Automatik-Laeufe blockt).
  // Die Mail-Karte (automation_emails_enabled) bleibt bewusst frei.
  const freeAutomationLocked =
    isFreeAutomationGateActive() && !enforcementLoading && planKnown && plan === 'free';
  const isLockedKey = (key: string) => freeAutomationLocked && key !== 'automation_emails_enabled';

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    supabase
      .from('profiles')
      .select('auto_optimize_new_enabled, auto_optimize_drift_enabled, automation_emails_enabled, auto_optimize_new_frequency, auto_optimize_drift_frequency, auto_upload_new_enabled, auto_upload_drift_enabled')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (!active) return;
        if (data) {
          setPrefs({
            auto_optimize_new_enabled: !!data.auto_optimize_new_enabled,
            auto_optimize_drift_enabled: !!data.auto_optimize_drift_enabled,
            automation_emails_enabled: !!data.automation_emails_enabled,
          });
          setFreqs({
            auto_optimize_new_frequency: isFrequency(data.auto_optimize_new_frequency) ? data.auto_optimize_new_frequency : 'monthly',
            auto_optimize_drift_frequency: isFrequency(data.auto_optimize_drift_frequency) ? data.auto_optimize_drift_frequency : 'monthly',
          });
          setUploads({
            auto_upload_new_enabled: !!data.auto_upload_new_enabled,
            auto_upload_drift_enabled: !!data.auto_upload_drift_enabled,
          });
        }
        setLoading(false);
      });
    return () => { active = false; };
  }, [user?.id]);

  const persist = async (patch: Record<string, boolean | string>, rollback: () => void) => {
    if (!user?.id) return;
    const { error } = await supabase.from('profiles').update(patch).eq('user_id', user.id);
    if (error) {
      rollback();
      toast.error(t('schedules.saveFailed'));
    } else {
      // Nur bei den Auto-Upload-Haken: die beiden Leser selektieren genau diese
      // zwei Spalten, ein Frequenz- oder E-Mail-Wechsel aendert fuer sie nichts.
      // Nach dem Rollback bewusst NICHT invalidieren — der Server hat den alten
      // Wert behalten, die Caches stimmen also weiterhin.
      if (touchesAutoUpload(patch)) {
        for (const queryKey of AUTO_UPLOAD_QUERY_KEYS) {
          void queryClient.invalidateQueries({ queryKey });
        }
      }
      toast.success(t('schedules.saved'));
    }
    setSaving(null);
  };

  // Free-Gate: die drei Update-Pfade brechen fuer gesperrte Schluessel VOR dem
  // optimistischen Set ab (kein persist, kein Toast). Die Controls sind zwar
  // disabled, aber ein programmatischer Aufruf (Tastatur-Race, stale Closure)
  // darf trotzdem nichts schreiben.
  const updateToggle = (key: ToggleKey, value: boolean) => {
    if (isLockedKey(key)) return;
    const prev = prefs[key];
    setSaving(key);
    setPrefs((p) => ({ ...p, [key]: value })); // optimistic
    void persist({ [key]: value }, () => setPrefs((p) => ({ ...p, [key]: prev })));
  };

  const updateFrequency = (key: FrequencyKey, value: Frequency) => {
    if (freeAutomationLocked) return;
    const prev = freqs[key];
    setSaving(key);
    setFreqs((f) => ({ ...f, [key]: value })); // optimistic
    void persist({ [key]: value }, () => setFreqs((f) => ({ ...f, [key]: prev })));
  };

  const updateUpload = (key: UploadKey, value: boolean) => {
    if (freeAutomationLocked) return;
    const prev = uploads[key];
    setSaving(key);
    setUploads((u) => ({ ...u, [key]: value })); // optimistic
    void persist({ [key]: value }, () => setUploads((u) => ({ ...u, [key]: prev })));
  };

  const automations: Array<{
    key: ToggleKey;
    icon: typeof Zap;
    title: string;
    desc: string;
    frequencyKey?: FrequencyKey;
    frequencyLabel?: string;
    uploadKey?: UploadKey;
    uploadLabel?: string;
  }> = [
    {
      key: 'auto_optimize_new_enabled', icon: Zap,
      title: t('schedules.autoNewLabel'), desc: t('schedules.autoNewDesc'),
      frequencyKey: 'auto_optimize_new_frequency', frequencyLabel: t('schedules.frequencyNewLabel'),
      uploadKey: 'auto_upload_new_enabled', uploadLabel: t('schedules.autoUploadNewLabel'),
    },
    {
      key: 'auto_optimize_drift_enabled', icon: RefreshCw,
      title: t('schedules.autoDriftLabel'), desc: t('schedules.autoDriftDesc'),
      frequencyKey: 'auto_optimize_drift_frequency', frequencyLabel: t('schedules.frequencyLabel'),
      uploadKey: 'auto_upload_drift_enabled', uploadLabel: t('schedules.autoUploadDriftLabel'),
    },
    {
      key: 'automation_emails_enabled', icon: Mail,
      title: t('schedules.emailLabel'), desc: t('schedules.emailDesc'),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={t('schedules.title')} subtitle={t('schedules.subtitle')} as={embedded ? 'h2' : 'h1'} />

      <div className="grid gap-4">
        {automations.map((a) => {
          const locked = isLockedKey(a.key);
          return (
          <Card key={a.key}>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <a.icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="text-base">{a.title}</CardTitle>
                </div>
                <Switch
                  checked={prefs[a.key]}
                  disabled={loading || saving === a.key || locked}
                  onCheckedChange={(v) => updateToggle(a.key, v)}
                  aria-label={a.title}
                />
              </div>
              <CardDescription className="pt-1">{a.desc}</CardDescription>
            </CardHeader>
            {a.frequencyKey && a.uploadKey && prefs[a.key] && (
              <CardContent className="pt-0 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <Label className="text-sm text-muted-foreground">{a.frequencyLabel}</Label>
                  <Select
                    value={freqs[a.frequencyKey]}
                    disabled={loading || saving === a.frequencyKey || locked}
                    onValueChange={(v) => updateFrequency(a.frequencyKey!, v as Frequency)}
                  >
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className={`flex items-start gap-3 ${locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary disabled:cursor-not-allowed"
                    checked={uploads[a.uploadKey]}
                    disabled={loading || saving === a.uploadKey || locked}
                    onChange={(e) => updateUpload(a.uploadKey!, e.target.checked)}
                    aria-label={a.uploadLabel}
                  />
                  <span className="text-sm">
                    <span className="font-medium">{a.uploadLabel}</span>
                    <span className="block text-xs text-muted-foreground pt-0.5">{t('schedules.autoUploadHint')}</span>
                  </span>
                </label>
              </CardContent>
            )}
            {locked && (
              <CardContent className="pt-0">
                {/* Free-Gate-Hinweis: Automatik gehoert ab dem Stichtag zu den
                    bezahlten Baendern. Eine Zeile pro gesperrter Karte, mit
                    CTA zu den Tarifen. */}
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <Lock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                  <span>
                    {t('schedules.freeGateHint')}{' '}
                    <Link
                      to="/pricing"
                      className="font-medium text-primary underline-offset-2 hover:underline"
                    >
                      {t('schedules.freeGateCta')}
                    </Link>
                  </span>
                </p>
              </CardContent>
            )}
          </Card>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">{t('schedules.quotaNote')}</p>
    </div>
  );
}
